import { getRecordsLibrary, type RecordItems } from "@/services/getRecordsLibrary";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useCustomParams } from "../useCustomParams";
import { useErrorHandler } from "@/hooks/useErrorHandler";

export const useGetRecordsLibrary = () => {
  const { page = 1, rowsPerPage, isAscending, orderByColumn, searchTerm } = useCustomParams();

  const { errorHandler } = useErrorHandler();

  function onError(error: AxiosError) {
    errorHandler(error, {
      eventName: "GET-RECORDINGS-LIST",
      showToast: true,
      properties: {
        endpoint: "/recording",
        reason: `"Error getting recordings list - ${error?.message}`,
        error: JSON.stringify(error),
      },
    });
  }

  const params: Record<string, string | number> = {
    pageSize: String(rowsPerPage),
    pageNumber: page,
  };

  if (orderByColumn) {
    params.orderByColumn = orderByColumn;
  }

  if (isAscending) {
    params.isAscending = String(isAscending);
  }

  if (searchTerm?.trim()) {
    params.search = searchTerm.trim();
  }

  const queryKey = ["recordingSubjects", rowsPerPage, page, orderByColumn, isAscending, searchTerm];

  const { data, isLoading, refetch, isError, error, failureCount, ...rest } = useQuery({
    queryKey,
    queryFn: async () => {
      try {
        return await getRecordsLibrary(params);
      } catch (error: unknown) {
        if (error instanceof AxiosError && failureCount === 3) {
          onError(error as AxiosError);
        }
        throw error;
      }
    },
  });

  return {
    data: data as RecordItems,
    isLoading,
    refetch,
    isError,
    error,
    ...rest,
  };
};
